<!-- eslint-disable vue/multi-word-component-names -->
<script setup lang="ts">
import { useEnvAttrsModifier } from "@/composables/tasks/environmental-modifier/useEnvAttrsModifier";
import FooterActions from "@/components/v2/Tasks/Vaccination/Buttons/ActionButtons/FooterActions.vue";
import { ref, watch } from 'vue';
import { TasksService } from "@/services/v2/tasks";
import { useTask } from "@/composables/tasks/useTask";
import { TaskTankAttribute } from "@/types/v2";

const { activeTask } = useTask();
const { tanks } = useEnvAttrsModifier();

// ── Expand / collapse ────────────────────────────────────────────────────────
const expandedIndices = ref<number[]>([]);

watch(tanks, (loadedTanks) => {
  if (loadedTanks.length && !expandedIndices.value.length) {
    expandedIndices.value = loadedTanks.map((_, i) => i);
    initEnteredValues();
  }
}, { immediate: true });

const toggleTank = (index: number) => {
  const pos = expandedIndices.value.indexOf(index);
  pos !== -1 ? expandedIndices.value.splice(pos, 1) : expandedIndices.value.push(index);
};
const isTankExpanded = (index: number) => expandedIndices.value.includes(index);

// ── Seed enteredValue on each attribute once tanks load ──────────────────────
const initEnteredValues = () => {
  for (const tank of tanks.value) {
    for (const attr of tank.attributes ?? []) {
      if (attr.enteredValue) continue;
      if (attr.name === 'light_regime') {
        attr.enteredValue = {
          light: attr.previousValue?.light ?? attr.target?.light ?? null,
          dark:  attr.previousValue?.dark  ?? attr.target?.dark  ?? null,
        };
      } else {
        attr.enteredValue = {
          value: attr.previousValue?.value ?? null,
          unit:  attr.previousValue?.unit ?? attr.unit ?? attr.units?.[0] ?? undefined,
        };
      }
    }
  }
};

// ── Helpers ──────────────────────────────────────────────────────────────────
const isLightRegime    = (attr: TaskTankAttribute) => attr.name === 'light_regime';
const hasRangeTarget   = (attr: TaskTankAttribute) =>
  !isLightRegime(attr) && attr.target != null && attr.target.min !== undefined;
const hasMultipleUnits = (attr: TaskTankAttribute) => (attr.units?.length ?? 0) > 1;

// ── Finish ───────────────────────────────────────────────────────────────────
const handleComplete = async () => {
  if (!activeTask.value) return;
  await TasksService.finishTask(activeTask.value);
};
</script>

<template>
  <div class="space-y-4">
    <h2 class="text-lg font-semibold text-gray-900">Tank-Environmental Attributes Modifier</h2>
    <p class="text-sm text-gray-500">Review the previous values and enter the new ones for each attribute.</p>

    <div
      v-for="(tank, index) in tanks"
      :key="tank.key"
      class="my-4 bg-gray-50 rounded-sm shadow-md ring-1 ring-gray-200 overflow-hidden"
    >
      <!-- Tank header -->
      <div
        @click="toggleTank(index)"
        class="p-4 cursor-pointer flex justify-between items-center hover:bg-gray-100 transition-colors"
      >
        <h3 class="text-base font-semibold text-gray-900">{{ tank.label }}</h3>
        <div class="flex items-center gap-2">
          <span class="text-sm text-gray-500">{{ tank.attributes?.length }} attributes</span>
          <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-gray-500 transition-transform"
               :class="{ 'rotate-180': !isTankExpanded(index) }"
               viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd" />
          </svg>
        </div>
      </div>

      <!-- Attributes list -->
      <div v-show="isTankExpanded(index)" class="divide-y divide-gray-100 border-t border-gray-200">

        <!-- Column headers -->
        <div class="grid grid-cols-12 gap-2 px-4 py-2 bg-gray-100 text-xs font-semibold text-gray-500 uppercase tracking-wide">
          <div class="col-span-3">Attribute</div>
          <div class="col-span-2 text-center">Target</div>
          <div class="col-span-3 text-center">Old value</div>
          <div class="col-span-1 text-center"></div>
          <div class="col-span-3 text-center">New value</div>
        </div>

        <div
          v-for="attr in tank.attributes"
          :key="attr.attributeId"
          class="grid grid-cols-12 gap-2 items-center px-4 py-4 bg-white hover:bg-gray-50"
        >
          <!-- Attribute name + intent badge -->
          <div class="col-span-3">
            <p class="font-medium text-gray-800 text-sm">{{ attr.label }}</p>
            <span
              class="inline-block mt-1 text-xs font-medium px-2 py-0.5 rounded-full"
              :class="attr.intent === 'adjust' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'"
            >
              {{ attr.intent === 'adjust' ? 'Adjust' : 'Record' }}
            </span>
          </div>

          <!-- ── Target ─────────────────────────────────────────────────── -->
          <div class="col-span-2 text-center text-sm">
            <!-- Light regime target -->
            <template v-if="isLightRegime(attr) && attr.target">
              <span class="text-blue-600 font-medium">{{ attr.target.light }}h / {{ attr.target.dark }}h</span>
              <p class="text-xs text-gray-400">light / dark</p>
            </template>
            <!-- Range target -->
            <template v-else-if="attr.target">
              <span class="text-blue-600 font-medium">{{ attr.target.min }} – {{ attr.target.max }}</span>
              <p class="text-xs text-gray-400">{{ attr.target.unit ?? attr.unit }}</p>
            </template>
            <span v-else class="text-xs text-gray-400 italic">Value Change</span>
          </div>

          <!-- ── OLD value ──────────────────────────────────────────────── -->
          <div class="col-span-3 text-center">
            <!-- Light regime previous -->
            <template v-if="isLightRegime(attr) && attr.previousValue">
              <span class="text-base font-semibold text-gray-700">{{ attr.previousValue.light }}h / {{ attr.previousValue.dark }}h</span>
              <p class="text-xs text-gray-400">light / dark</p>
            </template>
            <!-- Range / record previous -->
            <template v-else-if="attr.previousValue && attr.previousValue.value !== undefined">
              <span class="text-base font-semibold text-gray-700">{{ attr.previousValue.value }}</span>
              <span class="text-sm text-gray-500"> {{ attr.previousValue.unit }}</span>
            </template>
            <span v-else class="text-gray-300 text-sm italic">No history</span>
          </div>

          <!-- Arrow -->
          <div class="col-span-1 flex justify-center">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clip-rule="evenodd" />
            </svg>
          </div>

          <!-- ── NEW value (inputs) ─────────────────────────────────────── -->
          <div class="col-span-3">

            <!-- Light regime inputs -->
            <template v-if="isLightRegime(attr)">
              <div class="grid grid-cols-2 gap-1">
                <div>
                  <label class="block text-xs text-gray-400 mb-1">Light (h)</label>
                  <input
                    type="number" min="0" max="24"
                    v-model.number="attr.enteredValue!.light"
                    class="w-full border border-gray-300 rounded px-2 py-1.5 text-sm font-semibold text-primary focus:outline-none focus:border-primary"
                  />
                </div>
                <div>
                  <label class="block text-xs text-gray-400 mb-1">Dark (h)</label>
                  <input
                    type="number" min="0" max="24"
                    v-model.number="attr.enteredValue!.dark"
                    class="w-full border border-gray-300 rounded px-2 py-1.5 text-sm font-semibold text-primary focus:outline-none focus:border-primary"
                  />
                </div>
              </div>
            </template>

            <!-- Range / record inputs -->
            <template v-else>
              <div class="flex gap-1 items-center">
                <input
                  type="number"
                  v-model.number="attr.enteredValue!.value"
                  :placeholder="hasRangeTarget(attr) ? `${attr.target!.min}–${attr.target!.max}` : ''"
                  class="flex-1 min-w-0 border border-gray-300 rounded px-2 py-1.5 text-sm font-semibold text-primary focus:outline-none focus:border-primary"
                />
                <select
                  v-if="hasMultipleUnits(attr)"
                  v-model="attr.enteredValue!.unit"
                  class="border border-gray-300 rounded px-2 py-1.5 text-xs bg-white focus:outline-none focus:border-primary"
                >
                  <option v-for="u in attr.units" :key="u" :value="u">{{ u }}</option>
                </select>
                <span v-else-if="attr.unit" class="text-xs text-gray-400 shrink-0">{{ attr.unit }}</span>
              </div>
            </template>

          </div>
        </div>

      </div>
    </div>
  </div>

  <!-- Footer -->
  <FooterActions>
    <template #start>
      <button @click="() => {}" class="w-full">Instructions</button>
    </template>
    <template #end>
      <button @click="handleComplete" class="w-full">Finish task</button>
    </template>
  </FooterActions>
</template>
